import type { DataTableDownloadSlotProps } from './download-types.js';
import { downloadCSV, escapeForCSV } from '../../../../core/utils/download.js';
import { devLog } from '../../../../core/utils/logging.js';
import { convertArrayToCsv } from '../../../utils/download-utils.js';
import {
  DATA_TABLE_INDEX_COLUMN_HEADER,
  DATA_TABLE_DOWNLOAD_CSV_FILE_NAME,
} from '../../constants.js';
import type {
  Cell,
  Column,
  Header,
  Row,
  RowModel,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import type { DataTableRowData } from '../../public.api.js';

/**
 * DataTable slot definition for the Download action.
 * Columns may be optionally excluded from downloading by their IDs.
 * @public
 */
export function DataTableDownload(props: DataTableDownloadSlotProps) {
  return null;
}

/**
 * Extracts the column string representation for the download CSV.
 *
 * @param table - the table instance whose leaf headers are extracted.
 * @param includeIndexColumnHeader - if true, prepends an `index` column,
 * indicating the row's indentation, e.g. 1, 1.1, 1.2, 2, 2.1., ...
 * @param excludeColumns - array of column IDs that can optionally be excluded from the download.
 */
function getCsvHeaders<TData extends DataTableRowData>(
  table: Table<TData>,
  includeIndexColumnHeader: boolean,
  excludeColumns?: string[],
): string[] {
  const columnHeaders = table
    .getLeafHeaders()
    .filter((header) => {
      // If there are subheaders, we can also skip here. We work through
      // the remaining leaves later.
      if (header.subHeaders.length > 0) {
        return false;
      }

      // Don't include placeholder headers.
      if (header.isPlaceholder) {
        return false;
      }
      // Don't include built in columns in the download info.
      if (header.column.columnDef.meta?.builtin === true) {
        return false;
      }
      // Don't include columns explicitly excluded by the consumer in the download info.
      if (excludeColumns?.includes(header.column.id)) {
        return false;
      }
      // Don't include sparkline column in the download info.
      if (header.column.columnDef.columnType === 'sparkline') {
        return false;
      }
      if (header.column.getIsVisible() === false) {
        return false;
      }
      return true;
    })
    .map((header) => header._getDownloadString());

  if (includeIndexColumnHeader) {
    columnHeaders.unshift(DATA_TABLE_INDEX_COLUMN_HEADER);
  }
  return columnHeaders;
}

/**
 * @returns all visible cells of the given row as downloadable string, excluding built-in and sparkline columns.
 */
function getCsvCells<TData extends DataTableRowData>(
  row: Row<TData>,
  excludeColumns?: string[],
): string[] {
  return row
    .getVisibleCells()
    .filter(
      (cell) =>
        cell.column.columnDef.meta?.builtin !== true &&
        cell.column.columnDef.columnType !== 'sparkline' &&
        !excludeColumns?.includes(cell.column.id),
    )
    .map((cell) => cell._getDownloadString());
}

/**
 * @returns the given row's 1-based index, indicating its indentation level.
 * - Root rows have indices 1, 2, 3, 4, ...
 * - 2nd level sub-rows rows have indices 1.1, 1.2, 1.3, 2.1, 2.2, ...
 * - 3rd level sub-rows rows have indices 1.1.1, 1.1.2, 1.2.1, 1.3.1, 2.1.1, 2.1.2, ...
 * - etc.
 */
function getRowIndex<TData extends DataTableRowData>(row: Row<TData>) {
  let indexPrefix = '';
  for (const parent of row.getParentRows()) {
    indexPrefix = `${indexPrefix}${parent.index + 1}.`;
  }
  return `${indexPrefix}${row.index + 1}`;
}

/**
 * Extracts the headers and cell data string representation for the download CSV.
 * Utilizes async batching intended for large amounts of rows (e.g. 100k)
 * to unblock the main thread.
 *
 * If the table has sub-rows,  an `index` column is prepended,
 * indicating the row's indentation, e.g. 1, 1.1, 1.2, 2, 2.1., ...
 *
 * @param table - the table instance whose leaf headers are extracted.
 * @param rowModel - the rows to process.
 * @param excludeColumns - array of column IDs that can optionally be excluded from the download.
 */
async function getCsv<TData extends DataTableRowData>(
  table: Table<TData>,
  rowModel: RowModel<TData>,
  excludeColumns?: string[],
): Promise<string[][]> {
  const nrOfRows = rowModel.flatRows.length;
  const csvData: string[][] = Array.from({ length: nrOfRows + 1 }); // rows + header
  const batchSize = 1_000;
  let currentIndex = 0;
  const includeIndexColumn = table.getHasSubRows();
  csvData[0] = getCsvHeaders(table, includeIndexColumn, excludeColumns);

  function processBatch(): Promise<void> {
    return new Promise((resolve) => {
      const currentBatchEnd = Math.min(currentIndex + batchSize, nrOfRows);
      for (let i = currentIndex; i < currentBatchEnd; i++) {
        const row = rowModel.flatRows[i];
        const csvRow = getCsvCells(row, excludeColumns);
        if (includeIndexColumn) {
          const rowIndex = getRowIndex(row);
          csvRow.unshift(rowIndex);
        }
        csvData[i + 1] = csvRow;
      }
      currentIndex = currentBatchEnd;

      if (currentIndex < nrOfRows) {
        // yield control to the browser before continuing with next batch
        setTimeout(() => resolve(processBatch()), 0);
      } else {
        resolve();
      }
    });
  }

  await processBatch();
  return csvData;
}

function downloadData<TData extends DataTableRowData>(
  table: Table<TData>,
  rowModel: RowModel<TData>,
  subset: 'all' | 'page' | 'selected',
  excludeColumns?: string[],
  onDownloadEnd?: () => void,
): void {
  getCsv(table, rowModel, excludeColumns)
    .then((rows) => {
      const csvString = convertArrayToCsv(rows);
      downloadCSV(csvString, DATA_TABLE_DOWNLOAD_CSV_FILE_NAME);
      table.options.onDownloadData?.(
        subset,
        ...(excludeColumns ? [excludeColumns] : []),
      );
    })
    .finally(() => {
      onDownloadEnd?.();
    });
}

export const DataTableDownloadData: TableFeature = {
  createHeader<TData extends DataTableRowData, TValue>(
    header: Header<TData, TValue>,
    _table: Table<TData>,
  ) {
    header._getDownloadString = () => {
      const nestedHeaderNames: string[] = [];

      let currentColumnNesting: Column<TData, TValue> | undefined =
        header.column;

      // Go up the parent chain and find the correct names for the download names.
      while (currentColumnNesting !== undefined) {
        const columnDefHeader = currentColumnNesting.columnDef.header;
        // If the column definition defines either a custom element or nothing,
        // we call back to the header.id defined in the column def.
        if (
          typeof columnDefHeader === 'function' ||
          columnDefHeader === undefined
        ) {
          nestedHeaderNames.push(header.id);
        } else {
          nestedHeaderNames.push(columnDefHeader);
        }
        // Go up the nesting chain.
        currentColumnNesting = currentColumnNesting.parent;
      }

      return nestedHeaderNames.toReversed().join('.');
    };
  },

  createCell<TData extends DataTableRowData, TValue>(
    cell: Cell<TData, TValue>,
    column: Column<TData>,
    _row: Row<TData>,
    _table: Table<TData>,
  ) {
    cell._getDownloadString = () => {
      const cellValue = cell.getValue();

      // If there is no cell value, return an empty string.
      if (cellValue === undefined || cellValue === null) {
        return '';
      }

      // If the cell value is a Date object, return the iso string.
      if (cellValue instanceof Date) {
        return cellValue.toISOString();
      }

      // If the cell is in a columnType 'datetime', we should try to move it to a `Date` and export the iso string.
      if (
        (column.columnDef.columnType === 'datetime' ||
          // DEPRECATION: [APPDEV-16044] BREAKING-CHANGE: Remove 'date' columnType.
          column.columnDef.columnType === 'date') &&
        (typeof cellValue === 'string' || typeof cellValue === 'number')
      ) {
        // First try to actually parse it directly into a Date.
        // That should work for numbers and strings that have a correct date format.
        const cellDate = new Date(cellValue);
        if (!isNaN(cellDate.getTime())) {
          return cellDate.toISOString();
        }

        // Check for stringified timestamp values next.
        if (typeof cellValue === 'string' && !isNaN(parseInt(cellValue, 10))) {
          const cellDate = new Date(parseInt(cellValue, 10));
          if (!isNaN(cellDate.getTime())) {
            return cellDate.toISOString();
          }
        }

        // Unparsable string or number in the `columnType='datetime'` column.
        // Fall through to the remaining processing steps.
      }

      // If the cell renders a `MultiMeterBarChart`, we extract the values into JSON key-value pairs.
      // Regular `MeterBarChart` values are a simple number, which can be added directly.
      if (column.columnDef.columnType === 'meterbar') {
        // First, if it's a single `MeterBarChart` value, we return early.
        if (!Array.isArray(cellValue)) {
          return escapeForCSV(cellValue);
        }
        // Otherwise, when it's an array, it is a `MultiMeterBarChart`.
        try {
          const multiMeterBarValues = Object.fromEntries(
            cellValue.map((item) => [item.name, item.value]),
          );
          return escapeForCSV(JSON.stringify(multiMeterBarValues));
        } catch {
          devLog(
            `Failed to extract meterbar values for column "${column.id}". Expected array items to have 'name' and 'value' properties.`,
          );
        }
      }

      // If the cell value is of type string or is an array, return an escaped version of the value.
      if (typeof cellValue === 'string' || Array.isArray(cellValue)) {
        return escapeForCSV(cellValue);
      }

      // If the cell value is an object
      if (typeof cellValue === 'object' && cellValue !== null) {
        // if the object has a `toString` function defined, respect it
        if (
          Object.hasOwn(cellValue, 'toString') &&
          typeof cellValue.toString === 'function'
        ) {
          return escapeForCSV(cellValue.toString());
        }

        try {
          return escapeForCSV(JSON.stringify(cellValue));
        } catch {
          devLog(
            `Accessed cell value for column "${column.id}" received an object, but was not stringifyable. Consider providing a toString() function on the object.`,
          );
        }
      }

      return `${cellValue}`;
    };
  },

  createTable<TData extends DataTableRowData>(table: Table<TData>) {
    table.downloadAllData = (
      excludeColumns?: string[],
      onDownloadEnd?: () => void,
    ) =>
      downloadData(
        table,
        table.getCoreRowModel(),
        'all',
        excludeColumns,
        onDownloadEnd,
      );

    table.downloadPageData = (
      excludeColumns?: string[],
      onDownloadEnd?: () => void,
    ) =>
      downloadData(
        table,
        table.getPaginationRowModel(),
        'page',
        excludeColumns,
        onDownloadEnd,
      );

    table.downloadSelectedData = (
      excludeColumns?: string[],
      onDownloadEnd?: () => void,
    ) =>
      downloadData(
        table,
        table.getSelectedRowModel(),
        'selected',
        excludeColumns,
        onDownloadEnd,
      );
  },
};

/**
 * Configuration hook for download of the DataTable.
 * @internal
 */
export function useDownloadData<TData extends DataTableRowData>(
  props: {
    onDownloadData?: (
      subset: 'all' | 'page' | 'selected',
      excludedColumns?: string[],
    ) => void;
  },
  options: TableOptions<TData>,
): void {
  options.onDownloadData = props.onDownloadData;
}
